import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Collapse,
  Paper,
  Snackbar,
  Divider
} from '@mui/material';
import PropTypes from 'prop-types';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BugReportIcon from '@mui/icons-material/BugReport';

/**
 * A component for displaying error messages with details that can be expanded
 * and copied to clipboard
 */
const ErrorDialog = ({ 
  open, 
  onClose, 
  error, 
  title = "Error" 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  
  // Default error object if none provided
  const errorData = error || {
    message: 'An unknown error occurred',
    details: 'No additional details available',
    isRateLimit: false,
    statusCode: 500,
    fullError: null,
    timestamp: new Date().toISOString()
  };
  
  // Format error details to ensure objects are properly stringified
  const formatErrorDetails = (details) => {
    if (typeof details === 'object' && details !== null) {
      try {
        return JSON.stringify(details, null, 2);
      } catch {
        return String(details);
      }
    }
    return details;
  };

  // Handle copying error to clipboard
  const handleCopyToClipboard = () => {
    // Format the error details for copying
    const formattedDetails = typeof errorData.details === 'object' 
      ? formatErrorDetails(errorData.details) 
      : errorData.details;
    
    const errorText = JSON.stringify({
      message: errorData.message,
      details: formattedDetails,
      statusCode: errorData.statusCode,
      timestamp: errorData.timestamp || new Date().toISOString(),
      type: errorData.type || errorData.fullError?.type,
      traceback: errorData.fullError?.traceback || 'No traceback available'
    }, null, 2);
    
    // Copy to clipboard
    navigator.clipboard.writeText(errorText)
      .then(() => {
        setCopySnackbarOpen(true);
      })
      .catch(err => {
        console.error('Failed to copy error details:', err);
      });
  };
  
  // Get appropriate icon based on error type
  const getErrorIcon = () => {
    if (errorData.isRateLimit) {
      return <WarningAmberIcon color="warning" sx={{ fontSize: 28 }} />;
    }
    return <ErrorOutlineIcon color="error" sx={{ fontSize: 28 }} />;
  };
  
  // Format timestamp to more readable format
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };
  
  // Get user-friendly error title based on status code or error type
  const getErrorTitle = () => {
    if (errorData.isRateLimit) {
      return "Rate Limit Reached";
    } else if (errorData.statusCode) {
      if (errorData.statusCode === 404) return "Resource Not Found";
      if (errorData.statusCode === 401) return "Authentication Error";
      if (errorData.statusCode === 403) return "Permission Denied";
      if (errorData.statusCode >= 500) return "Server Error";
    }
    
    return title;
  };
  
  // Format error details for display
  const formattedDetails = formatErrorDetails(errorData.details);
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderTop: errorData.isRateLimit ? '4px solid #ED6C02' : '4px solid #d32f2f',
            },
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getErrorIcon()}
            <Typography variant="h6" component="div">
              {getErrorTitle()}
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {/* Primary user-friendly message */}
          <Typography variant="body1" gutterBottom sx={{ fontWeight: 'medium' }}>
            {errorData.message}
          </Typography>
          
          {/* Timestamp info */}
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
            Occurred at: {formatTimestamp(errorData.timestamp)}
          </Typography>
          
          {/* Toggle for more details */}
          <Button
            sx={{ mt: 1, textTransform: 'none' }}
            onClick={() => setExpanded(!expanded)}
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            size="small"
            color="inherit"
          >
            {expanded ? "Hide technical details" : "Show technical details"}
          </Button>
          
          <Collapse in={expanded}>
            <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'background.default' }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                  Technical Details
                </Typography>
                <Tooltip title="Copy all details to clipboard">
                  <IconButton onClick={handleCopyToClipboard} size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Basic error details */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Status Code</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{errorData.statusCode || 'N/A'}</Typography>
                
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Error Type</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{errorData.type || errorData.fullError?.type || 'Unknown'}</Typography>
                
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Details</Typography>
                <Box sx={{ 
                  p: 1, 
                  mt: 0.5,
                  mb: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  overflowX: 'auto',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  {formattedDetails}
                </Box>
              </Box>
              
              {/* Stack trace if available */}
              {errorData.fullError?.traceback && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <BugReportIcon fontSize="small" />
                    Stack Trace
                  </Typography>
                  <Box sx={{ 
                    p: 1, 
                    mt: 0.5,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    overflowX: 'auto',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    {errorData.fullError.traceback}
                  </Box>
                </Box>
              )}
            </Paper>
          </Collapse>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCopyToClipboard} 
            startIcon={<ContentCopyIcon />}
            color="inherit"
            size="small"
          >
            Copy Details
          </Button>
          <Button onClick={onClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Error details copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

ErrorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  error: PropTypes.object,
  title: PropTypes.string
};

export default ErrorDialog;